import axios, { type AxiosInstance } from 'axios';

import { createFanficsCatalogHeaders } from '@/lib/fanfics-catalog-api';
import type { AuthorWorksListResult, GetAuthorWorksParams } from '../types/author-works.types';
import { AUTHOR_WORKS_PAGE_LIMIT, buildAuthorWorksQuery } from './build-author-works-query.util';
import { normalizeAuthorWorksResponse } from './normalize-author-works.util';

/**
 * Empty published-works listings are valid SSR data. Only a failed request
 * should trigger a client refetch after hydration or logout.
 */
export function hasUsableAuthorWorksSsrData(initialLoadFailed?: boolean): boolean {
  return initialLoadFailed !== true;
}

function readHeader(
  headers: unknown,
  name: 'Authorization' | 'authorization',
): string | undefined {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  if ('get' in headers && typeof headers.get === 'function') {
    const value = (headers as { get: (key: string) => unknown }).get(name);
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  const value = (headers as Record<string, unknown>)[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function hasAuthorizationHeader(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const headers = error.config?.headers;
  return Boolean(readHeader(headers, 'Authorization') || readHeader(headers, 'authorization'));
}

/**
 * Public catalog calls should not fail because a stale session token was sent.
 * Retry without Authorization when the backend rejects the bearer token.
 */
export function shouldRetryAuthorWorksWithoutAuth(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (status !== 401 && status !== 403) {
    return false;
  }

  return hasAuthorizationHeader(error);
}

type FetchAuthorWorksListingParams = Pick<
  GetAuthorWorksParams,
  'userId' | 'page' | 'limit' | 'sortBy' | 'accessToken' | 'signal'
> & {
  client: AxiosInstance;
};

export async function fetchAuthorWorksListing({
  client,
  userId,
  page = 1,
  limit = AUTHOR_WORKS_PAGE_LIMIT,
  sortBy = 'newest',
  accessToken,
  signal,
}: FetchAuthorWorksListingParams): Promise<AuthorWorksListResult> {
  const url = `/api/fanfics-custom/v2?${buildAuthorWorksQuery({ userId, page, limit, sortBy })}`;

  try {
    const response = await client.get(url, {
      signal,
      headers: createFanficsCatalogHeaders(accessToken),
    });

    return normalizeAuthorWorksResponse(response.data, page, limit);
  } catch (error) {
    if (!shouldRetryAuthorWorksWithoutAuth(error)) {
      throw error;
    }

    // Instance defaults may still include Authorization; use a bare request.
    const response = await axios.get(url, {
      baseURL: client.defaults.baseURL,
      signal,
    });

    return normalizeAuthorWorksResponse(response.data, page, limit);
  }
}
